import { FilePdfOutlined, ShareAltOutlined } from '@ant-design/icons';
import {
  CommercePackingInfoSheetSubject,
  IssueTaskStatus,
  OrderStatusCode,
  PackingInfoProductEntry,
  PaperInvoice,
  ProductsForSupplierEntry,
} from '@gooduncles/gu-app-schema';
import { Button, Tabs, message } from 'antd';
import { groupBy } from 'lodash-es';
import { FC, useCallback, useEffect, useState } from 'react';
import { combineLatest, map } from 'rxjs';
import { issueTaskCategoriesForPackingInfoSheet } from 'src/constants/issue.constants';
import { useAuth } from 'src/stores/auth-context';

import { formatDate } from 'src/utils/date-util';
import {
  observeIssueTasks,
  observeOrder,
  observeStoreIssue,
  setPackingInfoSnapshot,
} from 'src/utils/firebase/firebase-short-cut';
import {
  buildPackingInfoSnapshot,
  downloadDailyProductList,
  getPackingInfoInventoryIds,
  getPackingInfoProductListForPartner,
  getProductListForPartner,
} from 'src/utils/handout-util';
import { generateDeliveryItemsForStore } from 'src/utils/packing-sheet-util';

import { useCommerceConf } from 'src/redux/hooks/useCommerceConf';
import { selectPartnersUsers } from 'src/redux/slices/partners';
import { useAppSelector } from 'src/redux/store';

import useSWRProductSuppliers from 'src/hooks/swr/useSWRProductSuppliers';
import useDeliveryRouteHistoryForDate from 'src/hooks/useDeliveryRouteHistoryForDate';
import useStores from 'src/hooks/useStores';
import { useTitle } from 'src/hooks/useTitle';

import classes from './PackingInfoSheetDashboard.module.scss';

import DatePickerWithArrows from '../Common/DatePickerWithArrows/DatePickerWithArrows';
import PrintWindow from '../PrintWindow/PrintWindow';
import EggProductList from './EggProductList/EggProductList';
import ProductsForSupplierList from './ProductsForSupplierList/ProductsForSupplierList';

/**
 * 기타 인쇄물 대시보드
 * FIXME: issue migration ✅
 *
 * - 상품별 인쇄물(계란, 대파, 마늘): 하루에 많은 물량이 들어오는 상품들을 물류팀에서 빠르게 분류할 때 사용
 * - 거래처별 인쇄물: 당일 거래처별 입고량을 현장 담당자가 확인할 때 사용
 * - 오늘의 주문품목: 모든 상품을 일괄적으로 확인하기 위해 사용.
 */
const PACKING_INFO_SUBJECT_LABEL: Record<string, string> = {
  [CommercePackingInfoSheetSubject.EGG]: '계란',
  [CommercePackingInfoSheetSubject.GREEN_ONION]: '대파',
  [CommercePackingInfoSheetSubject.GARLIC]: '마늘',
};

const PackingInfoSheetDashboard: FC = () => {
  useTitle('기타 인쇄물');
  const { date, setDate, dateForWhere, deliveryRouteHistory } = useDeliveryRouteHistoryForDate();
  const partnersUsers = useAppSelector(selectPartnersUsers);
  const stores = useStores();
  const { productSuppliers } = useSWRProductSuppliers([]);
  const { commerceConf } = useCommerceConf();
  const { authUser } = useAuth();
  const [sharing, setSharing] = useState(false);
  const [paperInvoices, setPaperInvoices] = useState<PaperInvoice[]>([]);
  const [greenOnionProductList, setGreenOnionProductList] = useState<PackingInfoProductEntry>({});
  const [eggProductList, setEggProductList] = useState<PackingInfoProductEntry>({});
  const [garlicProductList, setGarlicProductList] = useState<PackingInfoProductEntry>({});
  const [productsForSupplier, setProductsForSupplier] = useState<ProductsForSupplierEntry>({});
  const [issueKeysForSupplier, setIssueKeysForSupplier] = useState<Set<string>>(new Set());

  /**
   * 오늘의 주문품목을 다운로드한다.
   */
  const downloadDailyProductListPDF = useCallback(() => {
    downloadDailyProductList(dateForWhere, paperInvoices, productSuppliers ?? []);
  }, [dateForWhere, paperInvoices, productSuppliers]);

  /**
   * 파트너스 앱 공유용 스냅샷을 저장한다. 활성 탭과 무관하게 그날 주제 전부 + 거래처별 전부를 담는다.
   * 인쇄 버튼에도 붙어 있어 실패해도 인쇄는 진행된다.
   */
  const saveSnapshot = useCallback(async () => {
    if (paperInvoices.length === 0 || !productSuppliers || !authUser) return;
    try {
      const subjects = await Promise.all(
        Object.entries(commerceConf?.packingInfoSheet ?? {}).map(async ([key, conf]) => ({
          key,
          label: `${PACKING_INFO_SUBJECT_LABEL[key] ?? key} 상품별`,
          inventoryIds: await getPackingInfoInventoryIds(conf.supplierIds, conf.purchaseBundles),
        }))
      );
      const body = await buildPackingInfoSnapshot(paperInvoices, subjects, productSuppliers);
      await setPackingInfoSnapshot(dateForWhere, {
        date: dateForWhere,
        sharedAt: formatDate(new Date(), "yyyy-MM-dd'T'HH:mm:ss+0900"),
        sharedBy: { uid: authUser.uid, name: authUser.name, email: authUser.email },
        ...body,
      });
      message.success('파트너스에 공유했습니다.');
    } catch (e) {
      message.error('파트너스 앱 공유 저장에 실패했습니다.');
      throw e;
    }
  }, [paperInvoices, productSuppliers, authUser, commerceConf, dateForWhere]);

  const handleShare = useCallback(async () => {
    setSharing(true);
    await saveSnapshot().catch(() => undefined);
    setSharing(false);
  }, [saveSnapshot]);

  useEffect(() => {
    if (commerceConf) {
      if (paperInvoices.length > 0 && productSuppliers) {
        // 설정이 비어있는 환경(dev 등)에서도 거래처별 목록은 그려져야 한다.
        Object.entries(commerceConf.packingInfoSheet ?? {}).forEach(([subject, conf]) => {
          if (subject === CommercePackingInfoSheetSubject.EGG) {
            getPackingInfoProductListForPartner(paperInvoices, conf.supplierIds, conf.purchaseBundles).then((data) => {
              setEggProductList(data);
            });
          } else if (subject === CommercePackingInfoSheetSubject.GREEN_ONION) {
            getPackingInfoProductListForPartner(paperInvoices, conf.supplierIds, conf.purchaseBundles).then((data) => {
              setGreenOnionProductList(data);
            });
          } else if (subject === CommercePackingInfoSheetSubject.GARLIC) {
            getPackingInfoProductListForPartner(paperInvoices, conf.supplierIds, conf.purchaseBundles).then((data) => {
              setGarlicProductList(data);
            });
          }
        });
        getProductListForPartner(paperInvoices, productSuppliers).then(({ productForSupplierEntry, issueKeys }) => {
          setProductsForSupplier(productForSupplierEntry);
          setIssueKeysForSupplier(issueKeys);
        });
      } else {
        setEggProductList({});
        setProductsForSupplier({});
        setIssueKeysForSupplier(new Set());
      }
    }
  }, [commerceConf, paperInvoices, productSuppliers]);

  useEffect(() => {
    if (date && stores && deliveryRouteHistory) {
      const issueDate = formatDate(dateForWhere, "yyyy-MM-dd'T'00:00:00+0900");
      const acceptedOrdersObservable = observeOrder([['orderStatus', '==', OrderStatusCode.ACCEPTED]]);
      const deliveredOrdersObservable = observeOrder([
        ['orderStatus', '==', OrderStatusCode.DELIVERED],
        ['date', '==', dateForWhere],
      ]);
      const storeIssuesObservable = observeStoreIssue([['date', '==', issueDate]]).pipe(
        map((issues) =>
          issues
            .filter((issue) => issue.isDeleted !== true)
            .filter((issue) => issue.message && issue.status && ['완료', '입력'].includes(issue.status))
        )
      );
      const issueTasksObservable = observeIssueTasks([['targetDate', '==', dateForWhere]]).pipe(
        map((tasks) =>
          tasks.filter(
            (task) =>
              task.status === IssueTaskStatus.IN_PROGRESS &&
              issueTaskCategoriesForPackingInfoSheet.includes(task.category)
          )
        )
      );

      const subscription = combineLatest([
        storeIssuesObservable,
        acceptedOrdersObservable,
        deliveredOrdersObservable,
        issueTasksObservable,
      ]).subscribe(([storeIssues, acceptedOrders, deliveredOrders, issueTasks]) => {
        const deliveryItemsForStore = generateDeliveryItemsForStore(
          stores,
          [...acceptedOrders, ...deliveredOrders],
          storeIssues,
          issueTasks
        );

        const { date, deliverySpots } = deliveryRouteHistory;
        const dateKr = formatDate(date, 'yyyy년 L월 dd일');
        const groupByPartnerId = Object.entries(
          groupBy(
            deliverySpots.map((deliverySpot) => {
              // 배송 지점에 속한 매장
              const deliveryStoresWithItems = deliveryItemsForStore.filter(
                (item) => item.deliverySpotId === deliverySpot._id
              );
              return {
                ...deliverySpot,
                deliveryStoresWithItems,
              };
            }),
            'partnerId'
          )
        );

        const sortedDeliverySpots = groupByPartnerId
          .map(([partnerId, deliverySpots]) => {
            const partner = partnersUsers.find((partner) => partner._id === partnerId);
            return { partner, deliverySpots };
          })
          .sort(
            (a, b) => (a.partner?.deliveryPartnerStatus.sortKey ?? 0) - (b.partner?.deliveryPartnerStatus?.sortKey ?? 0)
          );

        setPaperInvoices(
          sortedDeliverySpots.flatMap(({ partner, deliverySpots }) => {
            // 담당자의 배송지 목록 -> 배송지 목록의 매장 목록
            return deliverySpots
              .map((spot) =>
                spot.deliveryStoresWithItems.map((item) => ({
                  ...item,
                  id: `${date}-${partner?._id}-${item.storeId}`,
                  partnerName: partner?.name ?? '미할당',
                  sortKey: partner?.deliveryPartnerStatus.sortKey ?? 0,
                  date: dateKr,
                }))
              )
              .flat();
          })
        );
      });

      return () => subscription.unsubscribe();
    } else {
      setPaperInvoices([]);
    }
  }, [dateForWhere, deliveryRouteHistory, date, stores, partnersUsers]);

  return (
    <div className={classes.packingInfoDashboardContainer}>
      <div className={classes.dashboardToolbar}>
        <DatePickerWithArrows date={date} setDate={setDate} />
        <Button
          type='primary'
          icon={<FilePdfOutlined />}
          disabled={paperInvoices.length === 0}
          onClick={downloadDailyProductListPDF}
        >
          {dateForWhere} 주문품목
        </Button>
        <Button
          icon={<ShareAltOutlined />}
          disabled={paperInvoices.length === 0}
          loading={sharing}
          onClick={handleShare}
        >
          파트너스 공유
        </Button>
      </div>
      <Tabs
        type='card'
        style={{ height: '100%', overflow: 'scroll' }}
        tabBarStyle={{ marginBottom: 0 }}
        items={[
          {
            key: 'eggProductList',
            label: '계란 상품별',
            children: (
              <PrintWindow onBeforePrint={saveSnapshot}>
                {eggProductList && <EggProductList data={eggProductList} />}
              </PrintWindow>
            ),
          },
          {
            key: 'greenOnionProductList',
            label: '대파 상품별',
            children: (
              <PrintWindow onBeforePrint={saveSnapshot}>
                {greenOnionProductList && <EggProductList data={greenOnionProductList} />}
              </PrintWindow>
            ),
          },
          {
            key: 'garlicProductList',
            label: '마늘 상품별',
            children: (
              <PrintWindow onBeforePrint={saveSnapshot}>
                {garlicProductList && <EggProductList data={garlicProductList} />}
              </PrintWindow>
            ),
          },
          {
            key: 'productsForSupplier',
            label: '거래처별',
            children: (
              <PrintWindow onBeforePrint={saveSnapshot}>
                {productsForSupplier && (
                  <ProductsForSupplierList data={productsForSupplier} issueKeys={issueKeysForSupplier} />
                )}
              </PrintWindow>
            ),
          },
        ]}
      />
    </div>
  );
};

export default PackingInfoSheetDashboard;
